import { memo, useCallback } from 'react';
import { SidebarSection, SectionTitle, SectionContent, FormGroup } from './FlightPlannerStyles';
import { DSP_LENSES } from '../constants/flightConstants';
import { calculatePlotAreaFromAltitude } from '../utils/flightCalculations';

/**
 * DSP Camera & Altitude settings section.
 * Lens selector, plot area slider, and flight height slider (bidirectional with plot area).
 */
const DspCameraSettings = ({ dspSettings, onUpdateDsp, currentAltitude }) => {
	const handleAltitudeChange = useCallback(
		altitude => {
			const newPlotArea = calculatePlotAreaFromAltitude(altitude, dspSettings.lens);
			onUpdateDsp('plotArea', Math.min(400000, Math.max(1, newPlotArea)));
		},
		[dspSettings.lens, onUpdateDsp]
	);

	return (
		<SidebarSection>
			<SectionTitle>Camera & Altitude</SectionTitle>
			<SectionContent>
				<FormGroup>
					<label>Camera Lens:</label>
					<select
						value={dspSettings.lens}
						onChange={e => onUpdateDsp('lens', e.target.value)}>
						{Object.entries(DSP_LENSES).map(([id, lens]) => (
							<option key={id} value={id}>
								{lens.name}
							</option>
						))}
					</select>
				</FormGroup>

				<FormGroup>
					<label>Plot Area (m²):</label>
					<div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
						<input
							type="range"
							min="1"
							max="400000"
							step="10"
							value={dspSettings.plotArea}
							onChange={e => onUpdateDsp('plotArea', parseInt(e.target.value))}
							style={{ flex: 1 }}
						/>
						<input
							type="number"
							min="1"
							max="400000"
							value={dspSettings.plotArea}
							onChange={e => {
								const raw = e.target.value;
								if (raw === '') {
									onUpdateDsp('plotArea', '');
									return;
								}
								const val = parseInt(raw);
								if (!isNaN(val))
									onUpdateDsp('plotArea', Math.min(400000, Math.max(1, val)));
							}}
							onBlur={() => {
								if (dspSettings.plotArea === '' || isNaN(dspSettings.plotArea))
									onUpdateDsp('plotArea', 15);
							}}
							style={{ width: '80px' }}
						/>
					</div>
					<small style={{ color: '#888', fontSize: '0.8rem' }}>
						Ground area captured in each DSP image
					</small>
				</FormGroup>

				<FormGroup>
					<label>Flight Height (m):</label>
					<div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
						<input
							type="range"
							min="10"
							max="500"
							step="5"
							value={Math.round(currentAltitude)}
							onChange={e => handleAltitudeChange(parseInt(e.target.value))}
							style={{ flex: 1 }}
						/>
						<input
							type="number"
							min="10"
							max="500"
							value={Math.round(currentAltitude)}
							onChange={e => {
								const raw = e.target.value;
								if (raw === '') {
									onUpdateDsp('plotArea', '');
									return;
								}
								const altitude = parseInt(raw);
								if (!isNaN(altitude)) handleAltitudeChange(altitude);
							}}
							onBlur={() => {
								if (dspSettings.plotArea === '' || isNaN(dspSettings.plotArea))
									onUpdateDsp('plotArea', 15);
							}}
							style={{ width: '70px' }}
						/>
					</div>
					<small style={{ color: '#888', fontSize: '0.8rem' }}>
						Set flight height - plot area adjusts automatically
					</small>
				</FormGroup>
			</SectionContent>
		</SidebarSection>
	);
};

export default memo(DspCameraSettings);
